// Визуальный редактор стартовой позиции: фигура из палитры → клетка доски.
// FEN остаётся внутренним форматом и пользователю не показывается.
import { SQUARES, type Square } from 'chess.js';

type PieceCode = 'wK' | 'wQ' | 'wR' | 'wB' | 'wN' | 'wP' | 'bK' | 'bQ' | 'bR' | 'bB' | 'bN' | 'bP';

const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR';
const CODES: PieceCode[] = ['wK', 'wQ', 'wR', 'wB', 'wN', 'wP', 'bK', 'bQ', 'bR', 'bB', 'bN', 'bP'];

function src(code: PieceCode): string { return `/pieces/${code}.png`; }

export class PositionEditor {
  readonly el: HTMLElement;
  private grid: HTMLElement;
  private pieces = new Map<Square, PieceCode>();
  private selected: PieceCode | null = 'wP';
  private orientation: 'white' | 'black' = 'white';

  constructor() {
    this.el = document.createElement('section');
    this.el.className = 'position-editor';
    this.el.innerHTML = `<div class="board-stage"><div class="mini-board-grid" data-grid></div></div>
      <div class="piece-palette" data-palette></div>
      <div class="row between setup-actions">
        <button type="button" class="ghost-btn" data-reset>↺</button>
        <button type="button" class="ghost-btn" data-clear>⌫</button>
        <button type="button" class="ghost-btn" data-flip>⇅</button>
      </div>`;
    this.grid = this.el.querySelector<HTMLElement>('[data-grid]')!;
    const palette = this.el.querySelector<HTMLElement>('[data-palette]')!;
    for (const code of CODES) {
      const b = document.createElement('button'); b.type = 'button'; b.dataset.piece = code;
      b.innerHTML = `<img src="${src(code)}" alt="${code}">`;
      b.addEventListener('click', () => { this.selected = code; this.paintPalette(); });
      palette.appendChild(b);
    }
    const erase = document.createElement('button'); erase.type = 'button'; erase.dataset.piece = 'erase'; erase.textContent = '×';
    erase.addEventListener('click', () => { this.selected = null; this.paintPalette(); }); palette.appendChild(erase);
    this.el.querySelector('[data-reset]')?.addEventListener('click', () => this.load(START));
    this.el.querySelector('[data-clear]')?.addEventListener('click', () => { this.pieces.clear(); this.render(); });
    this.el.querySelector('[data-flip]')?.addEventListener('click', () => { this.orientation = this.orientation === 'white' ? 'black' : 'white'; this.render(); });
    this.load(START);
  }

  placement(): string {
    const rows: string[] = [];
    for (let rank = 8; rank >= 1; rank--) {
      let row = ''; let empty = 0;
      for (let file = 0; file < 8; file++) {
        const code = this.pieces.get(`${String.fromCharCode(97 + file)}${rank}` as Square);
        if (!code) { empty++; continue; }
        if (empty) { row += empty; empty = 0; }
        const letter = code[1]!; row += code[0] === 'w' ? letter : letter.toLowerCase();
      }
      if (empty) row += empty; rows.push(row);
    }
    return rows.join('/');
  }

  private load(placement: string): void {
    this.pieces.clear();
    placement.split('/').forEach((row, ri) => {
      let file = 0;
      for (const c of row) {
        if (/\d/.test(c)) { file += Number(c); continue; }
        const color = c === c.toUpperCase() ? 'w' : 'b';
        this.pieces.set(`${String.fromCharCode(97 + file)}${8 - ri}` as Square, `${color}${c.toUpperCase()}` as PieceCode); file++;
      }
    });
    this.render();
  }

  private paintPalette(): void {
    this.el.querySelectorAll<HTMLElement>('[data-piece]').forEach((b) => b.classList.toggle('active', b.dataset.piece === (this.selected ?? 'erase')));
  }

  private render(): void {
    this.grid.replaceChildren();
    const order = this.orientation === 'black' ? [...SQUARES].reverse() : SQUARES;
    for (const square of order) {
      const file = square.charCodeAt(0) - 97; const rank = Number(square[1]);
      const b = document.createElement('button'); b.type = 'button'; b.dataset.square = square;
      b.className = `mini-square ${(file + rank) % 2 === 0 ? 'light' : 'dark'}`;
      const code = this.pieces.get(square);
      if (code) b.innerHTML = `<img src="${src(code)}" alt="${code}">`;
      b.addEventListener('click', () => { if (this.selected) this.pieces.set(square, this.selected); else this.pieces.delete(square); this.render(); });
      this.grid.appendChild(b);
    }
    this.paintPalette();
  }
}
